import type { MfaEnrollmentOffer } from '../api/auth';
import { Logger } from '../monitoring';
import { getSessionStorageItemSafely, removeSessionStorageItemSafely, setSessionStorageItemSafely } from '../utils/storage';

const MFA_ENROLLMENT_OFFER_KEY = 'mfa_enrollment_offer';

// Fallback for when sessionStorage itself throws (blocked storage, sandboxed iframes, some
// in-app browser WebViews). Doesn't survive a page refresh — only bridges the SPA navigation
// from Login/SignUp/MFAChallengePage to the enrollment offer page.
let inMemoryOffer: string | null = null;

export const stashMfaEnrollmentOffer = (offer: MfaEnrollmentOffer): void => {
  const serialized = JSON.stringify(offer);

  if (!setSessionStorageItemSafely(MFA_ENROLLMENT_OFFER_KEY, serialized)) {
    Logger.warn('sessionStorage unavailable for MFA enrollment offer, falling back to in-memory storage');
    inMemoryOffer = serialized;
  }
};

export const readStoredMfaEnrollmentOffer = (): MfaEnrollmentOffer | null => {
  const stored = getSessionStorageItemSafely(MFA_ENROLLMENT_OFFER_KEY) ?? inMemoryOffer;

  if (!stored) {
    return null;
  }

  try {
    return JSON.parse(stored) as MfaEnrollmentOffer;
  } catch {
    return null;
  }
};

export const clearStoredMfaEnrollmentOffer = (): void => {
  removeSessionStorageItemSafely(MFA_ENROLLMENT_OFFER_KEY);
  inMemoryOffer = null;
};

// hideMfaSkip's mere presence (any boolean) forces the offer; its value still governs Skip's visibility.
export const applyMfaVariantOverride = (
  offer: MfaEnrollmentOffer | undefined,
  hideMfaSkip: boolean | undefined
): MfaEnrollmentOffer | undefined =>
  hideMfaSkip !== undefined ? { eligible: true, available_factors: offer?.available_factors ?? ['phone'], hide_skip: hideMfaSkip } : offer;
